import { Content } from '@backstage/core-components';
import { catalogApiRef, useEntity, EntityRefLink } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    CircularProgress,
    Box,
    makeStyles,
    CardHeader,
    CardActions,
    Button,
    LinearProgress,
} from '@material-ui/core';
import { projectPageTranslationRef } from '../../translation/project-page/project-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { applicationPageTranslationRef } from '../../translation/application-page/application-page';
import { ApplicationForm } from './ApplicationForm';

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%',
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        transition: '0.2s',
        display: 'flex',
        flexDirection: 'column',

        '&:hover': {
            boxShadow: theme.shadows[4],
            transform: 'translateY(-2px)',
        },
    },

    header: {
        color: theme.getPageTheme({ themeId: 'Proyecto' }).fontColor,
        padding: theme.spacing(2, 2, 3),
        backgroundImage: theme.getPageTheme({ themeId: 'Proyecto' }).backgroundImage,
        backgroundPosition: 0,
        backgroundSize: 'inherit',
    },

    content: {
        flexGrow: 1,
    },

    actions: {
        paddingLeft: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },

    description: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        minHeight: 50,
    },
}));

export const ApplicationProjects = () => {
    const classes = useStyles();
    const { entity } = useEntity();
    const catalogApi = useApi(catalogApiRef);

    const tApp = useTranslationRef(applicationPageTranslationRef);
    const tProj = useTranslationRef(projectPageTranslationRef);

    const appRef = `application:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;

    const { loading, error, value: projects } = useAsync(async () => {
        const response = await catalogApi.getEntities({ filter: { kind: 'Project' } });
        const projects = response.items.filter((p: any) => p.spec?.applications?.includes(appRef))

        const parentProjects = projects.filter(e => !e.spec?.parent);

        // filtrar solo proyectos que tengan esta aplicación en spec.applications
        return parentProjects.length > 0 ? parentProjects : projects;
    }, [appRef, catalogApi]);

    if (loading) {
        return (
            <Content>
                <Box display="flex" justifyContent="center"><CircularProgress /></Box>
            </Content>
        );
    }

    if (error) {
        return (
            <Content>
                <Typography color="error">Error al cargar aplicaciones</Typography>
            </Content>
        );
    }

    return (
        <>
            <Grid item xs={12} style={{ marginBottom: 30 }}>
                <ApplicationForm />
            </Grid>
            <Typography variant="h5" gutterBottom>
                {tApp.t('applicationProjects.title')}
            </Typography>

            {(projects && projects?.length > 0) ? (
                <Grid container spacing={3}>
                    {projects?.map((proj: any) => (
                        <Grid item xs={12} sm={6} md={4} xl={3} key={proj.metadata.uid}>
                            <Card className={classes.card}>
                                <CardHeader
                                    className={classes.header}
                                    title={
                                        <Box>
                                            {proj.spec?.type && (
                                                <Typography variant="subtitle2" component="h3">
                                                    {proj.spec?.type}
                                                </Typography>
                                            )}
                                            {proj.metadata.title && (
                                                <Typography variant="h6" component="h4">
                                                    {proj.metadata.title}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <CardContent>

                                    {/* Número */}
                                    <Typography variant="body2" color="textSecondary">
                                        {tProj.t('projectTableColumns.number')}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        {proj.spec?.number ?? '—'}
                                    </Typography>

                                    {/* Ciclo de Vida */}
                                    <Typography variant="body2" color="textSecondary">
                                        {tProj.t('projectTableColumns.lifecycle')}
                                    </Typography>
                                    <Typography variant="body1">
                                        {proj.spec?.lifecycle ?? '—'}
                                    </Typography>

                                    {/* Project Manager */}
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2" color="textSecondary">{tProj.t('projectTableColumns.projectManager')}</Typography>
                                        {proj.spec?.projectManager ? <EntityRefLink
                                            entityRef={proj.spec?.projectManager as string}
                                            defaultKind={'User'}
                                            defaultNamespace="default"
                                        /> : <Typography variant="body1">
                                            {proj.spec?.projectManager as string || '—'}
                                        </Typography>}
                                    </Grid>

                                    {/* Execution Type */}
                                    <Typography variant="body2" color="textSecondary">{tProj.t('projectTableColumns.executionType')}</Typography>
                                    <Typography variant="body1">{proj.spec?.executionType as string || '—'}</Typography>

                                    {/* Percent Completed con barra */}
                                    <Typography variant="body2" color="textSecondary">{tProj.t('projectTableColumns.percentCompleted')}</Typography>
                                    <Box display="flex" alignItems="center">
                                        <LinearProgress
                                            variant="determinate"
                                            value={proj.spec?.percentCompleted as number}
                                            style={{ flexGrow: 1, marginRight: 8 }}
                                        />
                                        <Typography variant="body1"><strong>{proj.spec?.percentCompleted as string}%</strong></Typography>
                                    </Box>

                                </CardContent>

                                <CardActions className={classes.actions}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        component="a"
                                        href={`/catalog/${proj.metadata.namespace ?? 'default'}/${proj.kind.toLowerCase()}/${proj.metadata.name}`}
                                    >
                                        {tApp.t('applicationProjects.moreDetails')}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>) :
                <Typography>{tApp.t('applicationProjects.emptyProjects')}</Typography>}
        </>
    );
};
